import { Anchor, GridItemLayout, GridLayout, RadioButtonCard, TextField, ToggleCard, useMatchScreenWidth } from '@keplerco/core';
import React, { Fragment, useState } from 'react';
import classNames from 'classnames';
import { useAppActions, useAppState } from '../../../../overmind';
import { AssessmentType } from '../../../../enums/assessment-type';
import { SkeletonLoader } from '../../../../components/general/loading-state/loaders/skeleton-loader/skeleton-loader';
import { AssessmentRequest } from '../../../../overmind/actions';
import { invalidMaxScore, invalidMinScore } from '../../invalid-score';

enum ErrorMessage {
  NoPeerToggle = 'Activate at least one assessment toggle',
}

export function AssessmentPreferencesWidget({
  assessmentSlug,
  assessmentType,
  details,
  setDetails,
  onSaveAndContinue,
}: {
  assessmentSlug: string | undefined;
  assessmentType: AssessmentType;
  details: AssessmentRequest;
  setDetails: React.Dispatch<React.SetStateAction<AssessmentRequest>>;
  onSaveAndContinue: () => void;
}): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();

  const isMobile = useMatchScreenWidth('mobile');

  const [loading, setLoading] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [errorMessage] = useState<ErrorMessage>();

  if (loading) {
    return assessmentType === AssessmentType.PeerEndorsement ? (
      <Fragment>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <SkeletonLoader height="70px" />
          <SkeletonLoader height="70px" />
          <SkeletonLoader height="70px" />
        </div>

        <div style={{ marginTop: 75, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SkeletonLoader height="20px" />

          <SkeletonLoader height="20px" />

          <div>
            <SkeletonLoader />

            <SkeletonLoader />
          </div>
        </div>

        <div style={{ marginTop: 50, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
          <SkeletonLoader width="150px" height="25px" />
        </div>
      </Fragment>
    ) : (
      <Fragment>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SkeletonLoader width="100px" height="20px" />

          <SkeletonLoader height="70px" />
        </div>

        <div style={{ marginTop: 30, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SkeletonLoader width="150px" height="20px" />

          <div>
            <SkeletonLoader height="45px" />

            <SkeletonLoader height="45px" />
          </div>
        </div>

        <div style={{ marginTop: 75, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SkeletonLoader height="20px" />

          <SkeletonLoader height="20px" />

          <div>
            <SkeletonLoader />

            <SkeletonLoader />
          </div>
        </div>

        <div style={{ marginTop: 50, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
          <SkeletonLoader width="150px" height="25px" />
        </div>
      </Fragment>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 30 }}>
      {assessmentType !== AssessmentType.PeerEndorsement && (
        <Fragment>
          <div>
            <h4>Reassessment</h4>

            <div className="row">
              <ToggleCard
                id="allowReassessment"
                value={details?.allowReassessment ?? false}
                onChange={(_, value) => {
                  setDetails(currentState => {
                    const nextState = structuredClone(currentState);
                    nextState.allowReassessment = value;
                    return nextState;
                  });
                }}
              >
                <label htmlFor="allowReassessment" className="h3">
                  Allow reassessment
                </label>

                {!isMobile && (
                  <span className="body" style={{ color: 'var(--accent-2' }}>
                    Allow the learners to reset and retake the assessment by themselves
                  </span>
                )}
              </ToggleCard>
            </div>
          </div>

          <div>
            <h4>Assessment scores</h4>

            <div className="row">
              <div className="column">
                <TextField
                  label="Minimum score"
                  type="number"
                  defaultValue={details.minScore}
                  min={0}
                  onChange={event => {
                    setDetails(currentState => {
                      const nextState = structuredClone(currentState);
                      nextState.minScore = parseInt(event.target.value);
                      return nextState;
                    });
                  }}
                  responsive
                  validation={{
                    dirty: dirty,
                    invalid: invalidMinScore(details.minScore, details.maxScore),
                    message: 'Minimum score is required and must be less than maximum score and greater than 0',
                  }}
                />
              </div>

              <div className="column">
                <TextField
                  label="Maximum score"
                  type="number"
                  defaultValue={details.maxScore}
                  max={100}
                  onChange={event => {
                    setDetails(currentState => {
                      const nextState = structuredClone(currentState);
                      nextState.maxScore = parseInt(event.target.value);
                      return nextState;
                    });
                  }}
                  responsive
                  validation={{
                    dirty: dirty,
                    invalid: invalidMaxScore(details.minScore, details.maxScore),
                    message: 'Maximum score is required and must be greater than minimum score and less than 100',
                  }}
                />
              </div>
            </div>
          </div>
        </Fragment>
      )}

      <div>
        <h4>Do you want to force participants to complete this assessment once activated?</h4>

        {!isMobile && (
          <span className="body" style={{ color: 'var(--accent-2)', marginBottom: 10 }}>
            Force participants to complete an assessment, the next time someone logs in they will be required to complete the assessment before continuing.
          </span>
        )}

        <GridLayout columnCount={2}>
          <GridItemLayout>
            <RadioButtonCard
              id="forceAssessmentTrue"
              checked={!!details?.forceAssessment}
              onChange={() => {
                setDetails(currentState => {
                  const nextState = structuredClone(currentState);
                  nextState.forceAssessment = true;
                  return nextState;
                });
              }}
            >
              <div className="card">Yes, make this compulsory</div>
            </RadioButtonCard>
          </GridItemLayout>

          <GridItemLayout>
            <RadioButtonCard
              id="forceAssessmentFalse"
              checked={!details?.forceAssessment}
              onChange={() => {
                setDetails(currentState => {
                  const nextState = structuredClone(currentState);
                  nextState.forceAssessment = false;
                  return nextState;
                });
              }}
            >
              <div className="card">No, make optional</div>
            </RadioButtonCard>
          </GridItemLayout>
        </GridLayout>

        <span className={classNames('body', { invisible: !details.forceAssessment })} style={{ color: 'var(--accent-2)' }}>
          Great! We'll let people know to complete the assessment.
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
        <Anchor
          arrow
          onClick={async () => {
            setDirty(true);

            if (!assessmentSlug) return;
            if (!!errorMessage) return;
            if (assessmentType !== AssessmentType.PeerEndorsement && (details.minScore === undefined || details.maxScore === undefined || details.minScore >= details.maxScore || details.minScore < 0 || details.maxScore > 100)) return;

            setLoading(true);

            const request: AssessmentRequest = {
              ...details,
              companySlug: companyVariables.slug!,
            };

            await actions.updateAssessment(request).finally(async () => {
              const response = await actions.getAssessment(request.slug!);

              if (!!response) {
                setDetails({
                  ...response,
                  minScore: response.minScore ?? 0,
                  maxScore: response.maxScore ?? 0,
                  assessmentSubType: response.assessmentSubType
                });
              }
            });

            setLoading(false);

            onSaveAndContinue();
          }}
        >
          Save & Continue
        </Anchor>
      </div>
    </div>
  );
}
